import {Bid, Map, ProductDocument} from 'common'
import {DATA_INSERTION_PASSED, DATA_UPDATION_PASSED} from 'common/constants'
import {equipmentOptions, productStatus} from 'common/enums'
import {trimStringFields} from 'components/utils'
import {assign, isEmpty, isNull, isNumber} from 'lodash'
import {FirebaseInstances} from './helpers'
import {productsApi, productViewsApi, productBidsApi} from 'lib/api'
import {pickWritableProductFields} from 'lib/productWritePayload'

const getNestedMessage = (value: unknown) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined
  }

  const message = (value as Record<string, unknown>).message
  return typeof message === 'string' && message.trim() ? message : undefined
}

export const formatServiceError = (error: any) => {
  const responseData = error?.response?.data
  const candidates = [
    responseData?.data,
    getNestedMessage(responseData?.data),
    error?.body?.data,
    getNestedMessage(error?.body?.data),
    error?.details,
    getNestedMessage(error?.details),
    responseData?.msg,
    error?.message,
    error
  ]

  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length > 0) {
      return candidate.map(String).join('\n')
    }

    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate
    }
  }

  return 'Something went wrong.'
}

const sanitizeProductPayload = <T>(productData: T) =>
  pickWritableProductFields(
    trimStringFields(productData) as Record<string, any>
  )

const normalizeProductChoices = (product: ProductDocument) => {
  if (!product?.productDetails) return product

  const firstChoiceUnknown =
    product.productDetails.firstChoice === equipmentOptions.Unknown
  const secondChoiceUnknown =
    product.productDetails.secondChoice === equipmentOptions.Unknown

  return {
    ...product,
    productDetails: {
      ...product.productDetails,
      firstChoice: isNumber(product.equipmentHours)
        ? equipmentOptions.EquipmentHours
        : isNumber(product.equipmentMileage)
        ? equipmentOptions.EquipmentMileage
        : firstChoiceUnknown
        ? equipmentOptions.Unknown
        : 'Equipment Hours or Mileage',
      secondChoice: product.equipmentVinNumber
        ? equipmentOptions.EquipmentVINNumber
        : product.serialNumber
        ? equipmentOptions.SerialNumber
        : secondChoiceUnknown
        ? equipmentOptions.Unknown
        : 'Serial Number or VIN'
    }
  }
}

export function createProductService(_fb: FirebaseInstances) {
  const deleteProduct = async (productID: string) => {
    try {
      await productsApi.delete(productID)
      return {message: 'Product deleted successfully'}
    } catch (error: any) {
      const {response} = error
      const {status} = response || {}
      return {
        error: formatServiceError(error),
        errorCode: status || error?.status
      }
    }
  }

  const getNewProductCode = async () => {
    try {
      const result = await productsApi.list<{products: ProductDocument[]}>()
      const products = (result as any)?.products || (result as any)?.data || []
      const productCodes: number[] = products.map((p: ProductDocument) =>
        Number(p.productCode || '')
      )
      const randomGap = Math.floor(Math.random() * (50 - 10 + 1)) + 10
      if (Math.max(...productCodes) > 2100000) {
        return Math.max(...productCodes) + randomGap
      }
      return 2100000 + randomGap
    } catch {
      return 2100000 + Math.floor(Math.random() * 41) + 10
    }
  }

  const setProductDocument = async <T>({
    productData,
    isEdit,
    productId
  }: {
    productData: T
    isEdit?: boolean
    productId?: string
  }) => {
    try {
      if (isEdit && productId) {
        const result = await productsApi.update<ProductDocument>(productId, {
          ...sanitizeProductPayload(productData),
          hasAdminEdited: true
        })
        return {
          message: DATA_UPDATION_PASSED,
          equipment: result as ProductDocument,
          code: 200
        }
      }

      const result = await productsApi.create<ProductDocument>({
        ...sanitizeProductPayload(productData)
      })
      return {
        message: DATA_INSERTION_PASSED,
        equipment: result as ProductDocument,
        code: 201
      }
    } catch (err: any) {
      const {response} = err
      const {status} = response || {}
      return {error: formatServiceError(err), errorCode: status || err?.status}
    }
  }

  const updateProductDetails = async ({
    productDetails,
    productId
  }: {
    productId: string
    productDetails: {
      images?: Array<string>
      videos?: Array<string>
      driveUrls?: Array<string>
    }
  }) => {
    try {
      await productsApi.update(
        productId,
        sanitizeProductPayload({productDetails})
      )
      return {}
    } catch (error: any) {
      return {error: formatServiceError(error)}
    }
  }

  const getProductViewCount = async (productId: string) => {
    try {
      const result = await productViewsApi.getCount<{views: number}>(productId)
      const views =
        (result as any)?.views ??
        (result as any)?.viewCount ??
        (result as any)?.data?.views ??
        (result as any)?.data?.viewCount ??
        0
      return {views}
    } catch (error: any) {
      return {error}
    }
  }

  const getProductDocumentData = ({
    cancelCallback,
    documentId,
    updateState,
    callback
  }: {
    updateState: React.Dispatch<
      React.SetStateAction<{documentData: ProductDocument | null}>
    >
    callback?: (props: ProductDocument) => void
    cancelCallback: (error: Error) => void
    documentId: string
  }) => {
    const fetchProduct = async () => {
      try {
        const result = await productsApi.get<ProductDocument>(documentId)
        const product = normalizeProductChoices(result as ProductDocument)
        const viewResult = await getProductViewCount(product.productId || '')
        product.views =
          typeof viewResult?.views === 'number'
            ? viewResult.views
            : product.views || 0
        assign(product, {yearUnknown: isNull(product?.year)})
        updateState({documentData: {...product}})
        callback?.(product)
      } catch (e) {
        cancelCallback(e as Error)
      }
    }

    fetchProduct()
    return {unsSubData: () => {}}
  }

  const updateRequestedChanges = async (
    requestedChanges: Map,
    productId: string
  ) => {
    try {
      await productsApi.update(
        productId,
        sanitizeProductPayload({
          requestedChanges: isEmpty(requestedChanges) ? null : requestedChanges,
          status: isEmpty(requestedChanges)
            ? productStatus.Pending
            : productStatus.inProgress
        })
      )
      return {success: true}
    } catch (error) {
      return {error}
    }
  }

  const getProductBids = (
    productId: string,
    cancelCallback: (e: Error) => void,
    updateState: React.Dispatch<React.SetStateAction<Bid[] | undefined>>
  ) => {
    const fetchBids = async () => {
      try {
        const result = await productBidsApi.list<{data: Bid[]} | Bid[]>(
          productId
        )
        const bids = (result as any)?.data || result
        updateState(Array.isArray(bids) ? bids : [])
      } catch (e) {
        cancelCallback(e as Error)
      }
    }

    fetchBids()
    return () => {}
  }

  return {
    deleteProduct,
    getNewProductCode,
    setProductDocument,
    updateProductDetails,
    getProductViewCount,
    getProductDocumentData,
    updateRequestedChanges,
    getProductBids
  }
}
